import { useState, type ReactNode } from 'react';
import type { Dashboard, ScreenTile } from '@casa/model';
import { deepEqual } from '@casa/util';
import { bridge } from '../bridge/native_bridge.ts';
import { toaster } from '../ui/chakra.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { tileRect, type GridGeometry } from '../screens/grid.ts';
import type { ScreenEditing } from '../screens/screen_view.tsx';
import {
    addScreen,
    addTile,
    changeScreen,
    changeTile,
    deleteConnection,
    deleteOwnStyle,
    deleteScreen,
    deleteTiles,
    duplicateTile,
    moveScreen,
    moveToScreen,
    newScreenName,
    reorderTile,
    renameConnection,
    saveOwnStyle,
    setBackground,
    setConnection,
    setGrid,
    setTileStyle,
    tapsTo,
    type Draft,
    type NewTileStyle,
} from './dashboard_edits.ts';
import { ConnectionsDialog } from './connections_dialog.tsx';
import { CopyTileDialog } from './copy_tile_dialog.tsx';
import { colorsUsedIn } from '../forms/color_fields.tsx';
import { DashboardStyleDialog, ScreenPicker, ScreensDialog, TilePicker } from './edit_dialogs.tsx';
import { TileActions } from './tile_actions.tsx';
import { TileDialog } from './tile_dialog.tsx';
import { NewTileStyleDialog, presetsOf } from './tile_style_picker.tsx';
import type { CatalogType } from './tile_catalog.ts';

// Edit mode's state: the working copy (P-3), the selected tile, the open dialog; and what the
// buttons next to the selection, the side menu's edit buttons and the dialogs do. The dashboard app
// shows the working copy's screens and saves it.

type Dialog = 'tiles' | 'tileStyle' | 'screens' | 'move' | 'copy' | undefined | 'connections' | 'tile' | 'colors';

/** The tile dialog's tile, and the working copy from before, for Cancel. */
interface TileEditing {
    id: string;
    before: Draft;
    isNew: boolean;
    /** Added to a full screen, over other tiles: the dialog says so. */
    covers?: boolean;
}

interface Question {
    message: string;
    confirm: string;
    onConfirm: () => void;
}

let isEditModeRequested = false;

/** The dashboard that is loaded next opens in edit mode: a new one, made on this device (D-3). */
export function requestEditMode() : void {
    isEditModeRequested = true;
}

/** Whether edit mode was asked for; asks only once. */
export function takeEditModeRequest() : boolean {
    const requested = isEditModeRequested;
    isEditModeRequested = false;

    return requested;
}

export interface Editor {
    draft: Draft | null;
    begin: () => void;
    end: () => void;
    /** Edit mode on the shown screen, for the screen view. */
    screenEditing: ScreenEditing | undefined;
    openTilePicker: () => void;
    openScreens: () => void;
    /** The dashboard's connections (§7; in edit mode, the owner's choice). */
    openConnections: () => void;
    /** The dashboard's style: its background, grid and default tile style (T-2a, §5.11). */
    openColors: () => void;
    /** The open dialog or question, if any. */
    overlay: ReactNode;
}

/** How long the notice of a tile copied to another dashboard stays. */
const COPIED_NOTICE_MS = 8_000;

const nameOf = (tile: ScreenTile) : string => (tile.label ? `"${tile.label}"` : `this ${tile.type} tile`);

export function useEditor(saved: Dashboard | undefined, screenIndex: number, setScreenIndex: (index: number) => void, onSaveAs: (draft: Draft) => void) : Editor {
    const [draft, setDraft] = useState<Draft | null>(null);
    const [selectedId, setSelectedId] = useState<string>();
    const [dialog, setDialog] = useState<Dialog>();
    const [question, setQuestion] = useState<Question>();
    const [tileEditing, setTileEditing] = useState<TileEditing>();
    const [geometry, setGeometry] = useState<GridGeometry>();
    /** The type of the tile being added, while its style is picked (tile-styles.md TY-6). */
    const [addingType, setAddingType] = useState<Exclude<CatalogType, 'group'>>();

    const update = (change: (current: Draft) => Draft) : void => setDraft((current) => current && change(current));

    const end = () : void => {
        setDraft(null);
        setSelectedId(undefined);
        setDialog(undefined);
        setQuestion(undefined);
        setTileEditing(undefined);
    };

    const screen = draft?.dashboard.screens[screenIndex];

    /**
     * The toolbar next to the selected tile. On a fixed grid, where tiles may overlap, the order they
     * are drawn in too (L-8); not for a group, which is always drawn below the others.
     */
    const renderActions = () : ReactNode => {
        const tile = screen?.tiles.find((candidate) => candidate.id === selectedId);
        if (!tile || !draft) {
            return null;
        }

        return (
            <TileActions
                actions={[
                    {
                        label: 'Edit',
                        icon: 'pen',
                        onClick: () => {
                            setTileEditing({ id: tile.id, before: draft, isNew: false });
                            setDialog('tile');
                        },
                    },
                    {
                        label: 'Duplicate',
                        icon: 'clone',
                        onClick: () => {
                            const copied = duplicateTile(draft, screenIndex, tile.id);
                            setDraft(copied.draft);
                            setSelectedId(copied.id);
                        },
                    },
                    ...(draft.dashboard.grid.rows !== undefined && tile.type !== 'group'
                        ? [
                            { label: 'Bring to front', icon: 'bring-front', onClick: () => update((current) => reorderTile(current, screenIndex, tile.id, 'front')) },
                            { label: 'Send to back', icon: 'send-back', onClick: () => update((current) => reorderTile(current, screenIndex, tile.id, 'back')) },
                        ]
                        : []),
                    { label: 'Move to another screen', icon: 'arrow-right-to-bracket', onClick: () => setDialog('move') },
                    { label: 'Copy to another dashboard', icon: 'file-export', onClick: () => setDialog('copy') },
                    {
                        label: 'Delete',
                        icon: 'trash-can',
                        danger: true,
                        onClick: () =>
                            setQuestion({
                                message: `Delete ${nameOf(tile)}? There is no undo; Cancel in the menu throws away every change since editing started.`,
                                confirm: 'Delete',
                                onConfirm: () => {
                                    update((current) => deleteTiles(current, screenIndex, new Set([tile.id])));
                                    setSelectedId(undefined);
                                    setQuestion(undefined);
                                },
                            }),
                    },
                ]}
            />
        );
    };

    const screenEditing: ScreenEditing | undefined = draft
        ? {
            selectedId,
            onSelect: setSelectedId,
            onChange: (tiles: ScreenTile[]) =>
                update((current) => ({
                    ...current,
                    dashboard: { ...current.dashboard, screens: current.dashboard.screens.map((each, index) => (index === screenIndex ? { ...each, tiles } : each)) },
                })),
            renderActions,
            onGeometry: setGeometry,
        }
        : undefined;

    /** A new tile, then its configuration at once (E6a). */
    const add = (current: Draft, type: CatalogType, style?: NewTileStyle) : void => {
        const added = addTile(current, screenIndex, type, style);
        setDraft(added.draft);
        setSelectedId(added.id);
        setTileEditing({ id: added.id, before: current, isNew: true, covers: added.covers });
        setAddingType(undefined);
        setDialog('tile');
    };

    let overlay: ReactNode = null;
    if (question) {
        overlay = <ConfirmDialog message={question.message} confirm={question.confirm} cancel="Keep" onConfirm={question.onConfirm} onCancel={() => setQuestion(undefined)} />;
    } else if (draft && dialog === 'tiles') {
        overlay = (
            <TilePicker
                onClose={() => setDialog(undefined)}
                onPick={(type) => {
                    // A style step only where styles are for the type (tile-styles.md TY-1); none for a group.
                    if (type === 'group' || presetsOf(type, draft.dashboard).length === 0) {
                        add(draft, type);
                    } else {
                        setAddingType(type);
                        setDialog('tileStyle');
                    }
                }}
            />
        );
    } else if (draft && dialog === 'tileStyle' && addingType) {
        overlay = <NewTileStyleDialog type={addingType} dashboard={draft.dashboard} geometry={geometry} onPick={(style) => add(draft, addingType, style)} onClose={() => setDialog(undefined)} />;
    } else if (draft && dialog === 'tile' && tileEditing && bridge) {
        const tile = screen?.tiles.find((candidate) => candidate.id === tileEditing.id);
        const close = () : void => {
            setTileEditing(undefined);
            setDialog(undefined);
        };
        overlay = tile ? (
            <TileDialog
                shell={bridge}
                dashboard={draft.dashboard}
                tile={tile}
                tileSize={geometry ? tileRect(tile.layout, geometry) : undefined}
                screenName={screen?.name}
                isNew={tileEditing.isNew}
                // In the dialog, not a notice over its buttons.
                notice={tileEditing.covers ? 'The screen is full: the new tile is at the top left, over other tiles. Move it after Done, or make room.' : undefined}
                hasChanges={!deepEqual(draft.dashboard, tileEditing.before.dashboard)}
                onChange={(changed) => update((current) => changeTile(current, screenIndex, changed))}
                onSaveStyle={(style, previous) => update((current) => saveOwnStyle(current, style, previous))}
                onDeleteStyle={(name) => update((current) => deleteOwnStyle(current, tile.type, name))}
                onAddConnection={(name, connection) => update((current) => setConnection(current, name, connection))}
                onDone={close}
                onCancel={() => {
                    setDraft(tileEditing.before);
                    if (tileEditing.isNew) {
                        setSelectedId(undefined);
                    }
                    close();
                }}
            />
        ) : null;
    } else if (draft && dialog === 'move') {
        const id = selectedId;
        overlay = (
            <ScreenPicker
                screens={draft.dashboard.screens}
                current={screenIndex}
                onClose={() => setDialog(undefined)}
                onPick={(to) => {
                    if (id !== undefined) {
                        update((current) => moveToScreen(current, screenIndex, id, to));
                    }
                    setSelectedId(undefined);
                    setDialog(undefined);
                }}
            />
        );
    } else if (draft && dialog === 'copy' && bridge) {
        const tile = screen?.tiles.find((candidate) => candidate.id === selectedId);
        overlay = tile ? (
            <CopyTileDialog
                shell={bridge}
                tile={tile}
                connections={draft.dashboard.connections}
                onCopied={(where) => {
                    setDialog(undefined);
                    toaster.create({ description: `Copied ${nameOf(tile)} to ${where}.`, type: 'success', duration: COPIED_NOTICE_MS });
                }}
                onClose={() => setDialog(undefined)}
            />
        ) : null;
    } else if (draft && dialog === 'connections' && bridge) {
        overlay = (
            <ConnectionsDialog
                shell={bridge}
                dashboard={draft.dashboard}
                onKeep={(name, connection, previous) =>
                    update((current) => setConnection(previous !== undefined ? renameConnection(current, previous, name) : current, name, connection))}
                onDelete={(name) => update((current) => deleteConnection(current, name))}
                onClose={() => setDialog(undefined)}
            />
        );
    } else if (draft && dialog === 'colors') {
        // The grid is never made smaller than it is saved (DS-2): a new dashboard's own is its least.
        const least = saved?.grid ?? draft.dashboard.grid;
        overlay = (
            <DashboardStyleDialog
                background={draft.dashboard.background}
                grid={draft.dashboard.grid}
                least={least}
                style={draft.dashboard.tileStyle ?? {}}
                known={colorsUsedIn(draft.dashboard.screens)}
                onBackground={(background) => update((current) => setBackground(current, background))}
                onGrid={(grid) => update((current) => setGrid(current, grid, least))}
                onStyle={(style) => update((current) => setTileStyle(current, style))}
                onClose={() => setDialog(undefined)}
            />
        );
    } else if (draft && dialog === 'screens') {
        const screens = draft.dashboard.screens;
        overlay = (
            <ScreensDialog
                screens={screens}
                onClose={() => setDialog(undefined)}
                onSaveAs={() => {
                    setDialog(undefined);
                    onSaveAs(draft);
                }}
                onAdd={() => update((current) => addScreen(current, newScreenName(current.dashboard), undefined))}
                onRename={(index, name) => update((current) => changeScreen(current, index, { name }))}
                onIcon={(index, icon) => update((current) => changeScreen(current, index, { icon }))}
                onMove={(index, by) => {
                    update((current) => moveScreen(current, index, by));
                    if (index === screenIndex) {
                        setScreenIndex(index + by);
                    } else if (index + by === screenIndex) {
                        setScreenIndex(index);
                    }
                }}
                onDelete={(index) => {
                    const remove = () : void => {
                        update((current) => deleteScreen(current, index));
                        setSelectedId(undefined);
                        // The shown screen stays shown; if it was the one deleted, the one before it (or the new first).
                        setScreenIndex(screenIndex > index ? screenIndex - 1 : Math.min(screenIndex, screens.length - 2));
                        setQuestion(undefined);
                    };
                    const name = screens[index]!.name;
                    const count = screens[index]!.tiles.length;
                    // Taps to it go with it (tiles spec TA-5), unless another screen has its name too.
                    const taps = screens.some((screen, other) => other !== index && screen.name === name) ? 0 : tapsTo(draft.dashboard, name);
                    if (count === 0 && taps === 0) {
                        remove();

                        return;
                    }
                    const tiles = count === 0 ? '' : ` with its ${count} tile${count === 1 ? '' : 's'}`;
                    const tapping = taps === 0 ? '' : ` ${taps} tile${taps === 1 ? ' goes' : 's go'} to this screen on a tap; ${taps === 1 ? 'that tap goes' : 'those taps go'} too.`;
                    setQuestion({ message: `Delete the screen "${name}"${tiles}?${tapping}`, confirm: 'Delete', onConfirm: remove });
                }}
            />
        );
    }

    return {
        draft,
        begin: () => {
            if (saved) {
                setDraft({ dashboard: saved, origins: new Map() });
            }
        },
        end,
        screenEditing,
        openTilePicker: () => setDialog('tiles'),
        openScreens: () => setDialog('screens'),
        openConnections: () => setDialog('connections'),
        openColors: () => setDialog('colors'),
        overlay,
    };
}
